import * as THREE from 'three';
import Scroll from './scroll';
import gsap from "gsap";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import imagesLoaded from 'imagesloaded';
import FontFaceObserver from 'fontfaceobserver';
import fragment from './shaders/fragment.glsl';
import vertex from './shaders/vertex.glsl';


document.addEventListener("DOMContentLoaded", () => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) !== true) {
        new Sketch({
            dom: document.getElementById('webgl')
        })
    }
})

export default class Sketch {
    constructor(options) {
        this.time = 0
        this.container = options.dom
        this.scene = new THREE.Scene()

        this.width = this.container.offsetWidth
        this.height = this.container.offsetHeight

        this.camera = new THREE.PerspectiveCamera(75, this.width / this.height, 100, 2000)
        this.camera.position.z = 600
        this.camera.fov = 2 * Math.atan((this.height / 2) / 600) * (180 / Math.PI)

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });
        this.renderer.setSize(this.width, this.height)
        this.container.appendChild(this.renderer.domElement)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        this.controls = new OrbitControls(this.camera, this.renderer.domElement)
        this.controls.enableDamping = true

        this.images = [...document.querySelectorAll('.Project_Img img')]


        /*
        * Waiting until finish loading the Fonts
        */
        const SilentReaction = new Promise(resolve => {
            new FontFaceObserver("Silent-Reaction").load().then(() => {
                resolve()
            });
        })

        const inputSans = new Promise(resolve => {
            new FontFaceObserver("input-sans").load().then(() => {
                resolve()
            });
        })

        const linotypeSabon = new Promise(resolve => {
            new FontFaceObserver("linotype-sabon").load().then(() => {
                resolve()
            });
        })

        const preloadImages = new Promise((resolve, reject) => {
            imagesLoaded(
                document.querySelectorAll('img'),
                { background: true },
                resolve
            );
        });

        this.debouncedResize = debounce(this.resize.bind(this), 100);
        window.addEventListener('resize', this.debouncedResize);

        this.currentScroll = 0
        this.previousScroll = 0
        this.raycaster = new THREE.Raycaster()
        this.mouse = new THREE.Vector2()

        /**
         * When fonts & all images are loaded, then render three.js mesh, position etc...
        */
        let allDone = [SilentReaction, inputSans, linotypeSabon, preloadImages];
        Promise.all(allDone).then(() => {
            this.scroll = new Scroll()
            this.addImages()
            this.setPosition()
            this.mouseMovement()
            this.resize()
            this.setupResize()
            this.render()
            this.anchorLink()
        })
    }

    mouseMovement() {
        window.addEventListener('mousemove', (event) => {
            this.mouse.x = (event.clientX / this.width) * 2 - 1
            this.mouse.y = - (event.clientY / this.height) * 2 + 1

            // update the picking ray with the camera and mouse position
            this.raycaster.setFromCamera(this.mouse, this.camera)

            // calculate objects intersecting the picking ray
            const intersects = this.raycaster.intersectObjects(this.scene.children)

            if (intersects.length > 0) {
                let obj = intersects[0].object
                obj.material.uniforms.hover.value = intersects[0].uv
            }
        }, false)
    }


    setupResize() {
        window.removeEventListener('resize', this.resize.bind(this));
        window.addEventListener('resize', this.debouncedResize);
    }

    resize() {
        // Update sizes
        this.width = this.container.offsetWidth
        this.height = this.container.offsetHeight

        this.imageStore.forEach(object => {
            let bounds = object.img.getBoundingClientRect()

            object.top = bounds.top + this.scroll.scrollToRender,
                object.left = bounds.left,
                object.width = bounds.width,
                object.height = bounds.height
        })

        // Update camera
        this.camera.aspect = this.width / this.height
        this.camera.updateProjectionMatrix()

        // Update renderer
        this.renderer.setSize(this.width, this.height)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        this.setPosition()
    }


    /**
     * Merging mesh in html
     */
    addImages() {
        this.material = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                uImage: { value: 0 },
                hover: { value: new THREE.Vector2(0.5, 0.5) },
                hoverState: { value: 0 },
            },
            side: THREE.DoubleSide,
            fragmentShader: fragment,
            vertexShader: vertex,
        })

        this.materials = [];

        this.imageStore = this.images.map(img => {

            let bounds = img.getBoundingClientRect()
            let image = new Image()
            image.src = img.src

            let geometry = new THREE.PlaneGeometry(1, 1, 10, 10); // to implement resize, good for changing dimenstions
            let texture = new THREE.Texture(image)
            texture.needsUpdate = true;

            let material = this.material.clone()

            img.addEventListener('mouseenter', () => {
                gsap.to(material.uniforms.hoverState, {
                    duration: 1,
                    value: 1,
                    ease: "power3.out"
                })
            })
            img.addEventListener('mouseout', () => {
                gsap.to(material.uniforms.hoverState, {
                    duration: 1,
                    value: 0,
                    ease: "power3.out"
                })
            })

            this.materials.push(material)
            material.uniforms.uImage.value = texture


            let mesh = new THREE.Mesh(geometry, material)
            mesh.scale.set(bounds.width, bounds.height, 1)
            this.scene.add(mesh)

            return {
                img: img,
                mesh: mesh,
                top: bounds.top,
                left: bounds.left,
                width: bounds.width,
                height: bounds.height
            }
        })
    }


    /**
     * Adjusting positions(dimension) of html DOM images and threejs mesh
     */
    setPosition() {
        this.imageStore.forEach((o) => {
            o.mesh.position.y = this.currentScroll - o.top + this.height / 2 - o.height / 2
            o.mesh.position.x = o.left - this.width / 2 + o.width / 2
        })
    }

    anchorLink() {
        this.worksContainer = document.getElementById("works")
        this.urlHash = window.location.hash
        this.worksID = this.worksContainer.getAttribute('id')
        this.top = this.worksContainer.getBoundingClientRect().top
        this.viewportTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0

        if (this.worksContainer) {
            if (this.urlHash.indexOf(`${this.worksID}`) !== -1) {
                let delay = 0
                if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                    delay = 500
                }
                setTimeout(() => {
                    const scrollToPosition = this.viewportTop + this.top
                    window.scrollTo(0, scrollToPosition)
                }, delay)
            } else {
                return;
            }
        }
    }


    render() {
        this.time += 0.05

        this.scroll.render()
        this.currentScroll = this.scroll.scrollToRender

        if (Math.round(this.currentScroll) !== Math.round(this.previousScroll)) {
            // update the mesh position
            this.setPosition()

            this.materials.forEach((el) => {
                el.uniforms.time.value = this.time
            })
        }

        this.renderer.render(this.scene, this.camera);

        this.previousScroll = this.currentScroll

        requestAnimationFrame(this.render.bind(this))
    }
}

// Debounce function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}